import React, { useState } from "react";

import { ApiErrors } from "common/api-errors/errors";
import { CommonErrorDialog } from "common/components/CommonErrorDialog";
import { ContentWrapper } from "components/ContentWrapper";
import { Formik, Form } from "formik";
import { GetCurrentUser, SignUp } from "common/graphql/queryDescriptors";
import { useCustomQuery } from "common/store/hooks/useCustomQuery";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { CommonButton, CommonCheckbox, UserInfoField } from "common/components";

import {
  Typography,
  Container,
  LinearProgress,
  Grid,
  withWidth,
  isWidthDown,
  AppBar,
  makeStyles,
  Tabs,
  Tab,
  Box,
} from "@material-ui/core";

import "./Login.scss";

const appBarStyle = makeStyles({
  colorPrimary: {
    borderTopRightRadius: 25,
    borderTopLeftRadius: 25,
    boxShadow: "unset",
    position: "relative",
    top: "25px",
  },
});

const indicatorStyle = makeStyles({
  root: {
    borderTopRightRadius: 25,
    borderTopLeftRadius: 25,
  },
  indicator: {
    backgroundColor: "#0059E2",
    height: 4,
  },
});

const tabStyle = makeStyles({
  textColorInherit: {
    borderRadius: "0px !important",
  },
});

interface Values {
  email: string;
  password: string;
  confirm_password: string;
  cocNumber?: string;
  companyName?: string;
  agreementfinproag: boolean;
}

enum AccountType {
  Private = 0,
  Company = 1,
}

function Register({ width }: any) {
  //State
  const [accountType, setAccountType] = useState<AccountType>(
    AccountType.Private
  );
  const [error, setError] = useState("");

  //Hooks
  const history = useHistory();
  const { t } = useTranslation();
  const { execute: signUp } = useCustomQuery(
    SignUp({
      onError: (error: ApiErrors) => {
        if (error === ApiErrors.USER_DUPLICATE_EMAIL) {
          setError(t("Duplicate email"));
        }
      },
    })
  );

  //Actions
  useCustomQuery(
    GetCurrentUser({
      onSuccess: () => {
        history.push("/offerings");
      },
    })
  );

  //Options
  const initialValues: Values = {
    email: "",
    password: "",
    confirm_password: "",
    cocNumber: "",
    companyName: "",
    agreementfinproag: false,
  };

  const appBar = appBarStyle();
  const indicator = indicatorStyle();
  const tab = tabStyle();

  //Validation
  const onValidate = (values: Values) => {
    const errors: any = {};

    if (!values.password) {
      errors.password = t("Required");
    }

    if (!values.confirm_password) {
      errors.confirm_password = t("Required");
    } else if (values.password !== values.confirm_password) {
      errors.confirm_password = t("Passwords do not match");
    }

    if (!values.agreementfinproag) {
      errors.agreementfinproag = t("Required");
    }

    if (!values.email) {
      errors.email = t("Required");
    } else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.email)
    ) {
      errors.email = t("Invalid email address");
    }

    return errors;
  };

  const onSubmit = async (values: Values) => {
    setError("");

    if (accountType === AccountType.Private) {
      delete values.cocNumber;
      delete values.companyName;
    }

    await signUp({
      variables: values,
    });
  };

  return (
    <ContentWrapper>
      <Box className="login">
        <AppBar position="static" classes={appBar}>
          <Tabs
            centered
            variant="fullWidth"
            classes={indicator}
            value={accountType}
            onChange={(_: any, value: number) => setAccountType(value)}
            aria-label="simple tabs example"
          >
            <Tab
              label={t("Private")}
              classes={tab}
              id="simple-tab-0"
              aria-controls="simple-tabpanel-0"
            />

            <Tab
              label={t("Company")}
              classes={tab}
              id="simple-tab-1"
              aria-controls="simple-tabpanel-1"
            />
          </Tabs>
        </AppBar>

        <Formik
          initialValues={initialValues}
          validate={onValidate}
          onSubmit={onSubmit}
        >
          {({
            submitForm,
            isSubmitting,
            setFieldValue,
            values,
            errors,
            touched,
          }) => {
            const props = {
              values,
              errors,
              touched,
              setFieldValue,
              disableImmutable: false,
            };

            return (
              <Form>
                <Container
                  className={
                    isWidthDown("xs", width) ? "container--mobile" : "container"
                  }
                >
                  <Grid container spacing={6}>
                    <Grid item xs={12}>
                      <Typography className="title" color="primary">
                        {t("Register")}
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <UserInfoField id="email" {...props} />
                    </Grid>

                    <Grid item xs={12}>
                      <UserInfoField id="password" {...props} />
                    </Grid>

                    <Grid item xs={12}>
                      <UserInfoField id="confirm_password" {...props} />
                    </Grid>

                    {accountType === AccountType.Company && (
                      <Grid item xs={12}>
                        <UserInfoField id="cocNumber" {...props} />
                      </Grid>
                    )}

                    {accountType === AccountType.Company && (
                      <Grid item xs={12}>
                        <UserInfoField id="companyName" {...props} />
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <CommonCheckbox
                        id="agreementfinproag"
                        values={values}
                        errors={errors}
                        touched={touched}
                        setFieldValue={setFieldValue}
                        label={t(`I hereby consent to product updates`)}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      {isSubmitting ? (
                        <LinearProgress />
                      ) : (
                        <CommonButton
                          onClick={submitForm}
                          isSubmitting={isSubmitting}
                          values={values}
                          errors={errors}
                          touched={touched}
                          text={t("Sign up")}
                        />
                      )}
                    </Grid>

                    <Grid item xs={12}>
                      <Typography className="text">
                        {t("Already a member?")}{" "}
                        <span
                          className="text--link"
                          onClick={() => history.push("/authentication/login")}
                        >
                          {t("Login now")}
                        </span>
                      </Typography>
                    </Grid>
                  </Grid>
                </Container>
              </Form>
            );
          }}
        </Formik>

        <CommonErrorDialog error={error} setError={setError} />
      </Box>
    </ContentWrapper>
  );
}

export default withWidth()(Register);
